import React, { useMemo, useState } from 'react';
import { HTTPResponse } from '@/class/HTTPResponse';
import { useAuthContext } from '../../context/AuthContext';
import { fetchData } from '../../lib/api';
import { buildCsv, downloadCsv } from '../../lib/csv';
import {
  formatDateRangeForFilename,
  getDefaultReportDateRange,
  getJstDateStringFromDateTime,
  isDateWithinRange,
  validateReportDateRange,
} from '../../lib/reportDateRange';
import {
  adminButtonPrimary,
  adminCardAccent,
  adminInput,
  adminMutedText,
  adminSectionTitle,
} from '../layout/adminStyles';

type ReportType = 'page_views' | 'tweets' | 'completions' | 'special_sales';

interface ReportOption {
  value: ReportType;
  label: string;
}

const REPORT_OPTIONS: ReportOption[] = [
  { value: 'page_views', label: 'PV数の推移' },
  { value: 'tweets', label: 'つぶやき数の推移' },
  { value: 'completions', label: '更新完了日数の推移' },
  { value: 'special_sales', label: '特売商品ログ' },
];

const REPORT_FILE_PREFIX: Record<ReportType, string> = {
  page_views: 'pv',
  tweets: 'tweet',
  completions: 'completion',
  special_sales: 'special_sale',
};

const formatCsvDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Asia/Tokyo',
  });
};

const ReportCsvDownload: React.FC = () => {
  const { store } = useAuthContext();
  const defaultRange = useMemo(() => getDefaultReportDateRange(), []);
  const [selectedReport, setSelectedReport] = useState<ReportType>('page_views');
  const [startDate, setStartDate] = useState<string>(defaultRange.startDate);
  const [endDate, setEndDate] = useState<string>(defaultRange.endDate);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { minDate, maxDate } = defaultRange;

  const selectedLabel = useMemo(
    () => REPORT_OPTIONS.find((option) => option.value === selectedReport)?.label ?? '',
    [selectedReport]
  );

  const buildFilename = (reportType: ReportType): string => {
    const storeName = store?.name?.trim() || `store_${store?.id ?? 'unknown'}`;
    const safeStoreName = storeName.replace(/[\\/:*?"<>|]/g, '_');
    const rangeLabel = formatDateRangeForFilename(startDate, endDate);

    return `${safeStoreName}_${REPORT_FILE_PREFIX[reportType]}_${rangeLabel}.csv`;
  };

  const fetchReportCsv = async (
    reportType: ReportType,
    rangeStartDate: string,
    rangeEndDate: string
  ): Promise<{ headers: string[]; rows: (string | number)[][] }> => {
    if (!store?.id) {
      throw new Error('店舗情報が取得できません。');
    }

    switch (reportType) {
      case 'page_views': {
        const res: HTTPResponse = await fetchData(`store/${store.id}/daily-page-view/monthly`);
        const views = Array.isArray(res.data?.views) ? res.data.views : [];

        return {
          headers: ['日付', 'PV数'],
          rows: views
            .filter((view: { access_date: string }) =>
              isDateWithinRange(view.access_date, rangeStartDate, rangeEndDate)
            )
            .map((view: { access_date: string; view_count: number }) => [
              view.access_date,
              view.view_count,
            ]),
        };
      }
      case 'tweets': {
        const res: HTTPResponse = await fetchData(`store/${store.id}/tweet/monthly`);
        const counts = Array.isArray(res.data?.counts) ? res.data.counts : [];

        return {
          headers: ['日付', 'つぶやき数'],
          rows: counts
            .filter((item: { tweet_date: string }) =>
              isDateWithinRange(item.tweet_date, rangeStartDate, rangeEndDate)
            )
            .map((item: { tweet_date: string; tweet_count: number }) => [
              item.tweet_date,
              item.tweet_count,
            ]),
        };
      }
      case 'completions': {
        const res: HTTPResponse = await fetchData(`store/${store.id}/daily-completion/monthly`);
        const completions = Array.isArray(res.data?.completions) ? res.data.completions : [];

        return {
          headers: ['日付', '更新完了'],
          rows: completions
            .filter((item: { completion_date: string }) =>
              isDateWithinRange(item.completion_date, rangeStartDate, rangeEndDate)
            )
            .map((item: { completion_date: string; completed_count: number }) => [
              item.completion_date,
              item.completed_count === 1 ? '完了' : '未完了',
            ]),
        };
      }
      case 'special_sales': {
        const res: HTTPResponse = await fetchData(`store/${store.id}/special-sale/monthly`);
        const logs = Array.isArray(res.data?.logs) ? res.data.logs : [];

        return {
          headers: ['日時', '商品名', '価格', '単位'],
          rows: logs
            .filter((log: { started_at: string }) =>
              isDateWithinRange(getJstDateStringFromDateTime(log.started_at), rangeStartDate, rangeEndDate)
            )
            .map((log: { started_at: string; item_name: string; price: number; unit: string }) => [
              formatCsvDateTime(log.started_at),
              log.item_name,
              log.price,
              log.unit,
            ]),
        };
      }
      default:
        throw new Error('選択されたレポート種別が不正です。');
    }
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    if (value > endDate) {
      setEndDate(value);
    }
    setErrorMessage(null);
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
    if (value < startDate) {
      setStartDate(value);
    }
    setErrorMessage(null);
  };

  const handleDownload = async () => {
    if (!store?.id) {
      setErrorMessage('店舗情報が取得できません。');
      return;
    }

    const validationError = validateReportDateRange(startDate, endDate, minDate, maxDate);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsDownloading(true);
    setErrorMessage(null);

    try {
      const { headers, rows } = await fetchReportCsv(selectedReport, startDate, endDate);

      if (rows.length === 0) {
        setErrorMessage(`${selectedLabel}の指定期間にダウンロード対象データがありません。`);
        return;
      }

      const csv = buildCsv(headers, rows);
      downloadCsv(buildFilename(selectedReport), csv);
    } catch (err: unknown) {
      console.error(err);
      setErrorMessage('CSVのダウンロードに失敗しました。');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={`${adminCardAccent} bg-gradient-to-br from-white to-stone-50/80`}>
      <h4 className={`${adminSectionTitle} mb-2 border-l-stone-400 pb-0`}>CSVダウンロード</h4>
      <p className={`${adminMutedText} mb-4 text-sm`}>
        プルダウンで項目を選び、直近31日以内の期間を指定してCSV形式でダウンロードできます。
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="report-csv-start-date" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-stone-500">
            開始日
          </label>
          <input
            id="report-csv-start-date"
            type="date"
            value={startDate}
            min={minDate}
            max={maxDate}
            onChange={(event) => handleStartDateChange(event.target.value)}
            className={`${adminInput} w-full`}
            disabled={isDownloading}
          />
        </div>
        <div>
          <label htmlFor="report-csv-end-date" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-stone-500">
            終了日
          </label>
          <input
            id="report-csv-end-date"
            type="date"
            value={endDate}
            min={minDate}
            max={maxDate}
            onChange={(event) => handleEndDateChange(event.target.value)}
            className={`${adminInput} w-full`}
            disabled={isDownloading}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-3 sm:mt-4 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label htmlFor="report-csv-type" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-stone-500">
            ダウンロード項目
          </label>
          <select
            id="report-csv-type"
            value={selectedReport}
            onChange={(event) => setSelectedReport(event.target.value as ReportType)}
            className={`${adminInput} w-full`}
            disabled={isDownloading}
          >
            {REPORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={handleDownload}
          disabled={isDownloading || !store?.id}
          className={`${adminButtonPrimary} w-full sm:w-auto disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {isDownloading ? 'ダウンロード中...' : 'CSVをダウンロード'}
        </button>
      </div>

      {errorMessage ? (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
};

export default ReportCsvDownload;
